/**
 * @file theme_utils.js
 * @description Utilities for managing theme preferences with cookies
 */

// Cookie management utilities
export function setCookie(name, value, days = 365) {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
}

export function getCookie(name) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

// Theme management
export const THEME_COOKIE_NAME = 'memorize-life-theme';
export const THEME_OPTIONS = {
  LIGHT: 'light',
  DARK: 'dark', 
  SYSTEM: 'system'
};

export function getThemeFromCookie() {
  const savedTheme = getCookie(THEME_COOKIE_NAME);
  return savedTheme || THEME_OPTIONS.SYSTEM;
}

export function saveThemeToCookie(theme) {
  setCookie(THEME_COOKIE_NAME, theme);
}

export function getSystemThemePreference() {
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function getEffectiveTheme(themeSetting = null) {
  const theme = themeSetting || getThemeFromCookie();
  
  if (theme === THEME_OPTIONS.SYSTEM) {
    return getSystemThemePreference() ? THEME_OPTIONS.DARK : THEME_OPTIONS.LIGHT;
  }
  
  return theme;
}

export function applyThemeToDocument(theme) {
  const effectiveTheme = getEffectiveTheme(theme);
  
  // Remove all theme classes
  document.body.classList.remove('light', 'dark');
  
  // Add the correct theme class
  document.body.classList.add(effectiveTheme);
  
  return effectiveTheme;
}

export function initializeTheme() {
  const savedTheme = getThemeFromCookie();
  applyThemeToDocument(savedTheme);
  
  // Listen for system theme changes only if user has system preference
  if (savedTheme === THEME_OPTIONS.SYSTEM) {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => applyThemeToDocument(THEME_OPTIONS.SYSTEM);
    mediaQuery.addEventListener('change', handleChange);
    
    // Return cleanup function
    return () => mediaQuery.removeEventListener('change', handleChange);
  }
  
  return () => {}; // No cleanup needed
}

// Bible language management
export const BIBLE_LANGUAGE_COOKIE_NAME = 'memorize-life-bible-language';

export function getBibleLanguageFromCookie() {
  return getCookie(BIBLE_LANGUAGE_COOKIE_NAME);
}

export function saveBibleLanguageToCookie(language) {
  setCookie(BIBLE_LANGUAGE_COOKIE_NAME, language);
}
